import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaDraftingCompass } from '@react-icons/all-files/fa/FaDraftingCompass';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaSpinner } from '@react-icons/all-files/fa/FaSpinner';
import { FaSitemap } from '@react-icons/all-files/fa/FaSitemap';
import Breadcrumb, { BreadcrumbItem } from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../../commons/Confirm';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { approveDiseno } from '../../../../manager/workflow-def.reducer';
import WorkflowDesigner from '../diseno/workflow-designer';
import { getEmpresaDiseno, getEntity } from '../../../../manager/workflow-empresa.reducer';
import { reset, updateDiseno } from '../../../../manager/workflow-empresa-diseno.reducer';
import { playSound } from '../../../../shared/audio/audio';
import WorkflowEmpresaDisenoExportModal from './workflow-empresa-diseno-export-modal';
import {
    buildEmpresaDisenoTabContext,
    resolveEmpresaDisenoEmpresaId,
    resolveEmpresaDisenoEmpresaLabel,
    resolveEmpresaDisenoWorkflowId,
    resolveEmpresaDisenoWorkflowNombre,
} from '../diseno/workflow-diseno-context';

const CONFIG_EMPRESA_LIST = '/configuracion/workflow/workflow-empresa';
const APP_LIST = '/workflow';
const WRONG_SOUND = '/assets/audio/wrong.mp3';
const MAGIC_SOUND = '/assets/audio/magic.mp3';

function playWrongSound() {
    playSound(WRONG_SOUND);
}

function playMagicSound() {
    playSound(MAGIC_SOUND);
}

export interface WorkflowEmpresaDisenoPageProps {
    variant?: 'config' | 'app';
}

export default function WorkflowEmpresaDisenoPage({ variant = 'config' }: WorkflowEmpresaDisenoPageProps) {
    const { id, workflowEmpresaId } = useParams<{ id?: string; workflowEmpresaId?: string }>();
    const workflowCatalogId = variant === 'app' ? id : undefined;
    const empresaDisenoId = variant === 'app' ? workflowEmpresaId : id;
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // const isEmpresaDiseno = pathname.includes('/workflow-empresa/');

    // const parentIcon = isEmpresaDiseno ? <FaNetworkWired /> : ;

    const diseno = useAppSelector(s => s.workflowEmpresa.diseno);
    const workflowEmpresaEntity = useAppSelector(s => s.workflowEmpresa.entity);
    const loadingDiseno = useAppSelector(s => s.workflowEmpresa.loadingDiseno);
    const approving = useAppSelector(s => s.workflowEmpresa.approvingDiseno);
    const approveSuccess = useAppSelector(s => s.workflowEmpresa.approveSuccess);
    const errorMessage = useAppSelector(s => s.workflowEmpresaDiseno.errorMessage);
    const updateSuccess = useAppSelector(s => s.workflowEmpresaDiseno.updateSuccess);
    const updating = useAppSelector(s => s.workflowEmpresaDiseno.updating);

    const savePendingRef = useRef(false);
    const lastShownErrorRef = useRef<string | null>(null);

    const { alert, showToast } = usePageAlert();
    const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();
    const [exportOpen, setExportOpen] = useState(false);
    const [savedDesignVersion, setSavedDesignVersion] = useState(0);

    useEffect(() => {
        if (!empresaDisenoId) return;
        dispatch(getEmpresaDiseno(empresaDisenoId));
        dispatch(getEntity(empresaDisenoId));
        return () => { dispatch(reset()); };
    }, [empresaDisenoId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setConfirmLoading(approving); }, [approving, setConfirmLoading]);

    useEffect(() => {
        if (approveSuccess) {
            dismiss();
            showToast('success', 'Diseño aprobado correctamente.');
            if (empresaDisenoId) {
                dispatch(getEmpresaDiseno(empresaDisenoId));
                dispatch(getEntity(empresaDisenoId));
            }
            dispatch(reset());
        }
    }, [approveSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (updateSuccess) {
            if (savePendingRef.current) playMagicSound();
            savePendingRef.current = false;
            dismiss();
            showToast('success', 'Diseño modificado correctamente.');
            setSavedDesignVersion(v => v + 1);
            if (empresaDisenoId) {
                dispatch(getEmpresaDiseno(empresaDisenoId));
                dispatch(getEntity(empresaDisenoId));
            }
            dispatch(reset());
        }
    }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!errorMessage) {
            lastShownErrorRef.current = null;
            return;
        }
        if (lastShownErrorRef.current === errorMessage) return;

        if (savePendingRef.current && !updating) {
            playWrongSound();
            savePendingRef.current = false;
            lastShownErrorRef.current = errorMessage;
            showToast('error', errorMessage);
            return;
        }

        if (!savePendingRef.current) {
            lastShownErrorRef.current = errorMessage;
            showToast('error', errorMessage);
        }
    }, [errorMessage, updating]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadState = loadingDiseno ? 'loading'
        : errorMessage && !diseno?.id ? 'error'
            : diseno?.id ? 'loaded'
                : 'idle';

    const handleApprove = () => {
        if (empresaDisenoId) dispatch(approveDiseno(empresaDisenoId));
    };

    const confirmApprove = () => {
        ask(
            <>¿Confirmas que deseas aprobar el diseño <strong>{diseno?.nombre ?? `#${empresaDisenoId}`}</strong>? Esta acción no se puede deshacer.</>,
            handleApprove,
            { title: 'Aprobar Diseño', confirmLabel: 'Aprobar', variant: 'info' },
        );
    };


    const saveEntity = (data: any) => {
        const newEntity: any = {
            ...diseno,
            data: { ...diseno.data },
        };


        newEntity.data.edges = data.edges;
        newEntity.data.nodos = data.nodos;

        console.log('save entity ', newEntity);
        savePendingRef.current = true;
        dispatch(updateDiseno(newEntity));
    };

    const appBackPath = workflowCatalogId ? `/workflow/${workflowCatalogId}/config` : APP_LIST;
    const appVolverPath = workflowCatalogId && empresaDisenoId
        ? `/workflow/${workflowCatalogId}/config?workflowEmpresaId=${empresaDisenoId}`
        : workflowCatalogId
            ? `/workflow/${workflowCatalogId}/config`
            : APP_LIST;
    const volverPath = variant === 'app' ? appVolverPath : CONFIG_EMPRESA_LIST;

    const activeWorkflowEmpresa = workflowEmpresaEntity?.id != null
        && String(workflowEmpresaEntity.id) === String(empresaDisenoId)
        ? workflowEmpresaEntity
        : null;

    const tabContext = useMemo(
        () => buildEmpresaDisenoTabContext(diseno, activeWorkflowEmpresa, empresaDisenoId),
        [diseno, activeWorkflowEmpresa, empresaDisenoId],
    );

    const workflowIdForExport = useMemo(() => {
        if (workflowCatalogId) return workflowCatalogId;
        const workflowId = resolveEmpresaDisenoWorkflowId(diseno, activeWorkflowEmpresa, empresaDisenoId);
        return workflowId != null ? String(workflowId) : undefined;
    }, [workflowCatalogId, diseno, activeWorkflowEmpresa, empresaDisenoId]);

    const origenEmpresaId = resolveEmpresaDisenoEmpresaId(diseno, activeWorkflowEmpresa, empresaDisenoId);

    const origenEmpresaLabel = useMemo(
        () => resolveEmpresaDisenoEmpresaLabel(diseno, activeWorkflowEmpresa, empresaDisenoId),
        [diseno, activeWorkflowEmpresa, empresaDisenoId],
    );

    const resolvedWorkflowNombre = resolveEmpresaDisenoWorkflowNombre(
        diseno,
        activeWorkflowEmpresa,
        empresaDisenoId,
    );

    const canExportDiseno = loadState === 'loaded'
        && empresaDisenoId != null
        && workflowIdForExport != null;

    const breadcrumbItems: BreadcrumbItem[] = variant === 'app'
        ? [
            { label: 'Inicio', to: '/', icon: <FaHome /> },
            { label: 'Workflow', to: APP_LIST, icon: <FaProjectDiagram /> },
            ...(workflowCatalogId
                ? [{ label: 'Configuración de Plantillas', to: appBackPath, icon: <FaCog /> }]
                : []),
            { label: `Diseño personalizado #${empresaDisenoId ?? ''}`, icon: <FaDraftingCompass /> },
        ]
        : [
            { label: 'Inicio', to: '/', icon: <FaHome /> },
            { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
            { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
            { label: 'Worflow Empresa', to: CONFIG_EMPRESA_LIST, icon: <FaSitemap /> },
            { label: `Diseño #${empresaDisenoId ?? ''}`, icon: <FaDraftingCompass /> },
        ];

    return (
        <div style={s.page}>
            <Breadcrumb items={breadcrumbItems} />

            <PageHeader
                icon={<FaDraftingCompass />}
                iconColor="#7c3aed"
                title={resolvedWorkflowNombre ?? `Diseño Workflow #${empresaDisenoId ?? ''}`}
                subtitle={
                    origenEmpresaLabel
                        ? `Empresa: ${origenEmpresaLabel}`
                        : 'Actividades de Workflow'
                }
            />

            {/* Alert / confirm banners */}
            <Confirm config={confirmConfig} />
            {alert && <PageAlert alert={alert} />}

            <WorkflowEmpresaDisenoExportModal
                open={exportOpen}
                workflowEmpresaOrigenId={empresaDisenoId}
                workflowId={workflowIdForExport}
                origenEmpresaId={origenEmpresaId}
                origenEmpresaLabel={origenEmpresaLabel}
                onClose={() => setExportOpen(false)}
                showToast={showToast}
            />

            {/* Loading state */}
            {loadState === 'loading' && (
                <div style={s.stateBox}>
                    <FaSpinner style={{ fontSize: 28, color: '#7c3aed', animation: 'spin 1s linear infinite' }} />
                    <span style={s.stateText}>Cargando diseño…</span>
                </div>
            )}

            {/* Error state */}
            {loadState === 'error' && (
                <div style={s.stateBox}>
                    <span style={{ ...s.stateText, color: '#dc2626' }}>No se pudo cargar el diseño.</span>
                </div>
            )}
            {/* Content */}
            {loadState === 'loaded' && diseno &&
                <>
                    <WorkflowDesigner
                        key={empresaDisenoId}
                        entity={diseno.data}
                        saveEntity={(data: any) => { saveEntity(data); }}
                        volver={() => {
                            navigate(volverPath);
                        }}
                        onExport={canExportDiseno ? () => setExportOpen(true) : undefined}
                        resetModifiedKey={savedDesignVersion}
                        workflow={tabContext.workflow}
                        empresa={tabContext.empresa}
                        disenoPersonalizado={tabContext.disenoPersonalizado}
                        crearNodos={false} editarTipoNodo={false} editarActividad={false} permitirEliminarNodo={false}
                        permitirSeleccionarUsuario={variant !== 'app'}
                        mostrarConfigActividad={variant !== 'app'}
                        empresaDiseno={diseno}
                    />
                </>
            }
        </div>
    );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: any }) {
    if (!status) return null;
    const label = typeof status === 'object' ? status.nombre ?? JSON.stringify(status) : String(status);
    const colors: Record<string, { bg: string; text: string }> = {
        APROBADO: { bg: '#dcfce7', text: '#166534' },
        ACTIVO: { bg: '#dcfce7', text: '#166534' },
        PENDIENTE: { bg: '#fef9c3', text: '#854d0e' },
        BORRADOR: { bg: '#f1f5f9', text: '#475569' },
        MODIFICADO: { bg: '#fef3c7', text: '#92400e' },
    };
    const c = colors[label.toUpperCase()] ?? { bg: '#eff6ff', text: '#1e40af' };
    return (
        <span style={{
            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: c.bg, color: c.text, letterSpacing: '0.04em',
        }}>
            {label}
        </span>
    );
}

function renderValue(val: any): React.ReactNode {
    if (val === null || val === undefined) return <span style={{ color: '#94a3b8' }}>—</span>;
    if (typeof val === 'boolean') return val ? '✓' : '✗';
    if (typeof val === 'object') {
        if (val.nombre) return `${val.nombre}${val.id ? ` (#${val.id})` : ''}`;
        if (val.id) return `#${val.id}`;
        return JSON.stringify(val);
    }
    return String(val);
}

// ── Styles ─────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
    page: { display: 'flex', flexDirection: 'column', gap: 24 },
    stateBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '48px 0' },
    stateText: { fontSize: 14, color: '#64748b' },

    card: {
        background: '#fff', borderRadius: 14, padding: '24px 28px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', gap: 20,
    },
    cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    cardTitle: { fontSize: 14, fontWeight: 700, color: '#0f172a' },

    fieldGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '12px 24px',
    },
    field: { display: 'flex', flexDirection: 'column', gap: 2 },
    fieldLabel: { fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' },
    fieldValue: { fontSize: 14, color: '#1e293b' },

    toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 4 },
    btnBack: {
        display: 'flex', alignItems: 'center', padding: '9px 18px',
        background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
        borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    btnApprove: {
        display: 'flex', alignItems: 'center', padding: '10px 22px',
        background: '#7c3aed', color: '#fff', border: 'none',
        borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(124,58,237,0.3)',
    },
};
